import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { RosterTeeType } from "../../enums";
import { TEE_UNASSIGNED_LABEL, teeLabel } from "../helpers";

/**
 * Swatch fills for the tees that name a colour. The other four (Junior, Skill,
 * Custom, Member) name a *category*, so they get a hollow swatch rather than an
 * invented colour — the row keeps its alignment either way.
 *
 * The colour is redundant with the label beside it, never the only carrier of
 * meaning, so it stays a small dot: the label holds full text contrast in both
 * themes instead of being tinted down to meet a hue.
 */
const TEE_SWATCH: Partial<Record<RosterTeeType, string>> = {
  [RosterTeeType.RED]: "bg-red-600",
  [RosterTeeType.WHITE]: "bg-white",
  [RosterTeeType.BLUE]: "bg-blue-600",
  [RosterTeeType.BLACK]: "bg-zinc-900",
  [RosterTeeType.GOLD]: "bg-amber-400",
};

const swatchClassName = cn(
  "size-2.5 shrink-0 rounded-full ring-1 ring-inset ring-foreground/55",
);

/** Colored ball for a named tee. Category tees stay hollow. */
export function TeeBall({ teeType }: { teeType: RosterTeeType }) {
  const swatch = TEE_SWATCH[teeType];
  return (
    <span
      aria-hidden
      className={cn(
        "inline-block",
        swatchClassName,
        swatch ?? "bg-transparent",
      )}
    />
  );
}

/** Color dot with the tee name to its right. */
export function TeeSwatch({
  teeType,
  className,
}: {
  teeType?: RosterTeeType | null;
  className?: string;
}) {
  if (teeType == null) {
    return (
      <span className={cn("text-xs italic text-text-muted", className)}>
        {TEE_UNASSIGNED_LABEL}
      </span>
    );
  }

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 text-xs text-text-muted",
        className,
      )}
    >
      <TeeBall teeType={teeType} />
      <span>{teeLabel(teeType)}</span>
    </span>
  );
}

export function TeeBadge({ teeType }: { teeType?: RosterTeeType | null }) {
  if (teeType == null) {
    return (
      <span className="text-xs italic text-text-muted">
        {TEE_UNASSIGNED_LABEL}
      </span>
    );
  }

  return (
    <Badge variant="outline" className="gap-1.5 text-xs font-medium">
      <TeeBall teeType={teeType} />
      {teeLabel(teeType)}
    </Badge>
  );
}
